import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError, fmtDate } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { FileText, Download, Loader2, FileSignature, ReceiptText, Mail, History, CheckCircle2, BellRing, Clock } from "lucide-react";

const AUDIT_LABEL = { view: "Consultation de la page", pdf_view: "Vue / téléchargement du PDF", signed: "Signature du devis" };

const fcfa = (n) => (n == null ? "—" : Number(n).toLocaleString("fr-FR") + " FCFA");

function parseFilename(res, fallback) {
  const cd = res.headers?.["content-disposition"] || "";
  const m = cd.match(/filename=([^;]+)/i);
  return m ? m[1].trim() : fallback;
}
async function downloadBlob(url, fallbackName) {
  const res = await api.get(url, { responseType: "blob" });
  const u = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = u; a.download = parseFilename(res, fallbackName); a.click();
  URL.revokeObjectURL(u);
}

export function CommercialDocs() {
  const [busy, setBusy] = useState("");
  const [confidentiel, setConfidentiel] = useState(false);
  const [devis, setDevis] = useState({ profil: "moyen", offre: "lancement", lms: false, maintenance_pct: "0", client: "", contact: "" });
  const [history, setHistory] = useState([]);
  const [mail, setMail] = useState(null); // {kind, numero, confidentiel, title}
  const [mailTo, setMailTo] = useState("");
  const [mailMsg, setMailMsg] = useState("");
  const [audit, setAudit] = useState(null); // {numero, events}

  const openAudit = async (numero) => {
    setAudit({ numero, events: null });
    try { const { data } = await api.get(`/admin/commercial/devis/${numero}/audit`); setAudit({ numero, events: data.events }); }
    catch (e) { toast.error(apiError(e)); setAudit(null); }
  };

  const load = useCallback(() => {
    api.get("/admin/commercial/devis").then(({ data }) => setHistory(data.devis || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const dl = async (key, url, name) => {
    setBusy(key);
    try { await downloadBlob(url, name); }
    catch (e) { toast.error(apiError(e, "Document indisponible")); }
    finally { setBusy(""); }
  };

  const genDevis = async () => {
    const q = new URLSearchParams({
      profil: devis.profil, offre: devis.offre, lms: String(devis.lms),
      maintenance_pct: devis.maintenance_pct, client: devis.client, contact: devis.contact,
    }).toString();
    await dl("devis", `/admin/commercial/devis.pdf?${q}`, "devis-vitalis-sih.pdf");
    load();
  };

  const openMail = (m) => { setMail(m); setMailTo(""); setMailMsg(""); };
  const toggleSign = async (d) => {
    try { await api.post(`/admin/commercial/devis/${d.numero}/sign`, { signed: !d.signed_at }); toast.success(!d.signed_at ? "Devis marqué comme signé" : "Signature annulée"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remind = async (d) => {
    try { const { data } = await api.post(`/admin/commercial/devis/${d.numero}/remind`); toast.success(`Relance envoyée à ${data.to}`); setTimeout(load, 1500); }
    catch (e) { toast.error(apiError(e, "Relance impossible")); }
  };
  const sendMail = async () => {
    setBusy("mail");
    try {
      await api.post("/admin/commercial/send", { kind: mail.kind, to: mailTo, numero: mail.numero || "", confidentiel: !!mail.confidentiel, message: mailMsg });
      toast.success(`Document envoyé à ${mailTo}`);
      setMail(null);
    } catch (e) { toast.error(apiError(e, "Envoi impossible")); }
    finally { setBusy(""); }
  };

  const selCls = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-navy focus:border-[#0E7C7B] focus:outline-none";

  return (
    <div data-testid="commercial-docs" className="space-y-8">
      <div>
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-navy">
          <FileText size={20} className="text-[#0E7C7B]" /> Documents commerciaux — Vitalis Digital SIH
        </h2>
        <p className="mt-1 text-sm text-slate-500">Documents PDF prêts à envoyer (français, FCFA, branding Cabinet CARIS Santé).</p>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5" data-testid="commercial-fiche">
            <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-[#0E7C7B]/10"><FileText size={20} className="text-[#0E7C7B]" /></div>
            <p className="font-display font-semibold text-navy">Fiche commerciale (1 page)</p>
            <p className="mt-1 flex-1 text-sm text-slate-500">Modules inclus, tarifs early adopter (années 1 & 2) et avantages.</p>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => dl("fiche", "/admin/commercial/fiche.pdf", "fiche-commerciale-vitalis-sih.pdf")} disabled={busy === "fiche"} data-testid="commercial-dl-fiche" className="flex-1 rounded-full bg-[#0E7C7B] text-white hover:bg-[#0b6463]">
                {busy === "fiche" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Download size={15} className="mr-1" />} PDF
              </Button>
              <Button onClick={() => openMail({ kind: "fiche", title: "Fiche commerciale" })} variant="outline" data-testid="commercial-mail-fiche" className="rounded-full border-[#0E7C7B] text-[#0E7C7B]"><Mail size={15} className="mr-1" /> Envoyer</Button>
            </div>
          </div>

          <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5" data-testid="commercial-proposition">
            <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-[#0E7C7B]/10"><FileSignature size={20} className="text-[#0E7C7B]" /></div>
            <p className="font-display font-semibold text-navy">Proposition commerciale complète</p>
            <p className="mt-1 flex-1 text-sm text-slate-500">Contexte, modules, tarifs, plan de démarrage, point mort et signature.</p>
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs text-slate-600" data-testid="commercial-confidentiel-toggle">
              <input type="checkbox" checked={confidentiel} onChange={(e) => setConfidentiel(e.target.checked)} className="h-4 w-4 accent-[#0E7C7B]" />
              Filigrane « Confidentiel » (envoi prospect)
            </label>
            <div className="mt-3 flex gap-2">
              <Button onClick={() => dl("prop", `/admin/commercial/proposition.pdf${confidentiel ? "?confidentiel=true" : ""}`, "proposition-commerciale-vitalis-sih.pdf")} disabled={busy === "prop"} data-testid="commercial-dl-proposition" className="flex-1 rounded-full bg-[#0E7C7B] text-white hover:bg-[#0b6463]">
                {busy === "prop" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Download size={15} className="mr-1" />} PDF
              </Button>
              <Button onClick={() => openMail({ kind: "proposition", title: "Proposition commerciale", confidentiel })} variant="outline" data-testid="commercial-mail-proposition" className="rounded-full border-[#0E7C7B] text-[#0E7C7B]"><Mail size={15} className="mr-1" /> Envoyer</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="commercial-devis-form">
        <h3 className="flex items-center gap-2 font-display text-base font-semibold text-navy">
          <ReceiptText size={18} className="text-[#0E7C7B]" /> Générateur de devis officiel
        </h3>
        <p className="mt-1 text-sm text-slate-500">Le devis est numéroté automatiquement et valable 30 jours.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Profil d'établissement</label>
            <select className={selCls} value={devis.profil} onChange={(e) => setDevis({ ...devis, profil: e.target.value })} data-testid="devis-profil">
              <option value="petit">Petit hôpital</option>
              <option value="moyen">Hôpital moyen</option>
              <option value="chu">Grand établissement (CHU)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Offre tarifaire</label>
            <select className={selCls} value={devis.offre} onChange={(e) => setDevis({ ...devis, offre: e.target.value })} data-testid="devis-offre">
              <option value="lancement">Offre de lancement (années 1-2)</option>
              <option value="standard">Tarif standard (dès l'année 3)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Maintenance</label>
            <select className={selCls} value={devis.maintenance_pct} onChange={(e) => setDevis({ ...devis, maintenance_pct: e.target.value })} data-testid="devis-maintenance">
              <option value="0">Aucune</option>
              <option value="20">20 % du forfait annuel</option>
              <option value="25">25 % du forfait annuel</option>
              <option value="30">30 % du forfait annuel</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600" data-testid="devis-lms-toggle">
              <input type="checkbox" checked={devis.lms} onChange={(e) => setDevis({ ...devis, lms: e.target.checked })} className="h-4 w-4 accent-[#0E7C7B]" />
              Inclure l'option LMS
            </label>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Établissement client</label>
            <Input value={devis.client} onChange={(e) => setDevis({ ...devis, client: e.target.value })} placeholder="Ex : Hôpital Général d'Abidjan" data-testid="devis-client" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Contact (optionnel)</label>
            <Input value={devis.contact} onChange={(e) => setDevis({ ...devis, contact: e.target.value })} placeholder="Ex : Dr Kouassi" data-testid="devis-contact" />
          </div>
        </div>
        <Button onClick={genDevis} disabled={busy === "devis"} data-testid="devis-generate" className="mt-5 rounded-full bg-navy text-white hover:bg-navy-light">
          {busy === "devis" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <ReceiptText size={15} className="mr-1" />} Générer le devis PDF
        </Button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="commercial-devis-history">
        <h3 className="flex items-center gap-2 font-display text-base font-semibold text-navy">
          <History size={18} className="text-[#0E7C7B]" /> Historique des devis ({history.length})
        </h3>
        {history.length === 0 ? (
          <p className="mt-3 text-sm text-slate-400">Aucun devis émis pour le moment.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase text-slate-400">
                <tr><th className="py-2">Numéro</th><th>Client</th><th>Montant/an</th><th>Date</th><th>Statut</th><th className="text-right">Actions</th></tr>
              </thead>
              <tbody>
                {history.map((d) => (
                  <tr key={d.numero} className="border-t border-slate-100" data-testid={`devis-row-${d.numero}`}>
                    <td className="py-2 font-mono text-xs text-navy">{d.numero}</td>
                    <td className="text-navy">{d.client || "—"}</td>
                    <td className="font-semibold text-[#0E7C7B]">{fcfa(d.total)}</td>
                    <td className="text-slate-500">{fmtDate(d.created_at)}</td>
                    <td data-testid={`devis-status-${d.numero}`}>
                      {d.signed_at ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><CheckCircle2 size={12} /> Signé</span>
                      ) : d.last_email ? (
                        <span className="text-[11px] text-slate-500" title={d.last_email.to}>
                          <span className={`mr-1 inline-block h-2 w-2 rounded-full ${d.last_email.status === "sent" ? "bg-teal-500" : "bg-amber-400"}`} />
                          Envoyé le {fmtDate(d.last_email.sent_at)}{d.reminded_at ? " · relancé" : ""}
                        </span>
                      ) : <span className="text-[11px] text-slate-400">Non envoyé</span>}
                    </td>
                    <td className="text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => dl(`re-${d.numero}`, `/admin/commercial/devis/${d.numero}.pdf`, `devis-${d.numero}.pdf`)} title="Télécharger" data-testid={`devis-dl-${d.numero}`} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><Download size={15} /></button>
                        <button onClick={() => openMail({ kind: "devis", numero: d.numero, title: `Devis ${d.numero}` })} title="Envoyer par email" data-testid={`devis-mail-${d.numero}`} className="rounded-lg p-1.5 text-[#0E7C7B] hover:bg-[#0E7C7B]/10"><Mail size={15} /></button>
                        {d.last_email && !d.signed_at && (
                          <button onClick={() => remind(d)} title="Relancer le prospect" data-testid={`devis-remind-${d.numero}`} className="rounded-lg p-1.5 text-amber-500 hover:bg-amber-50"><BellRing size={15} /></button>
                        )}
                        <button onClick={() => toggleSign(d)} title={d.signed_at ? "Annuler la signature" : "Marquer comme signé"} data-testid={`devis-sign-${d.numero}`} className={`rounded-lg p-1.5 hover:bg-emerald-50 ${d.signed_at ? "text-emerald-600" : "text-slate-400"}`}><CheckCircle2 size={15} /></button>
                        <button onClick={() => openAudit(d.numero)} title="Journal d'audit" data-testid={`devis-audit-${d.numero}`} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><Clock size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!mail} onOpenChange={(o) => !o && setMail(null)}>
        <DialogContent data-testid="commercial-mail-dialog">
          <DialogHeader><DialogTitle>Envoyer par email — {mail?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Email du prospect</label>
              <Input type="email" value={mailTo} onChange={(e) => setMailTo(e.target.value)} placeholder="contact@etablissement.com" data-testid="mail-to" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Message (optionnel)</label>
              <textarea value={mailMsg} onChange={(e) => setMailMsg(e.target.value)} rows={3} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#0E7C7B] focus:outline-none" placeholder="Un mot d'accompagnement…" data-testid="mail-message" />
            </div>
            <Button onClick={sendMail} disabled={busy === "mail" || !mailTo} data-testid="mail-send" className="w-full rounded-full bg-[#0E7C7B] text-white hover:bg-[#0b6463]">
              {busy === "mail" ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Mail size={15} className="mr-1" />} Envoyer le document
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!audit} onOpenChange={(o) => !o && setAudit(null)}>
        <DialogContent data-testid="commercial-audit-dialog">
          <DialogHeader><DialogTitle>Journal d'audit — {audit?.numero}</DialogTitle></DialogHeader>
          {!audit?.events ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#0E7C7B]" size={22} /></div>
          ) : audit.events.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">Aucun événement enregistré pour ce devis.</p>
          ) : (
            <ol className="max-h-96 space-y-3 overflow-y-auto pr-1">
              {audit.events.map((e, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`audit-event-${i}`}>
                  <span className={`mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full ${e.event === "signed" ? "bg-emerald-500" : e.event === "pdf_view" ? "bg-[#0E7C7B]" : "bg-slate-300"}`} />
                  <div>
                    <p className="text-sm font-medium text-navy">{AUDIT_LABEL[e.event] || e.event}{e.meta?.by ? ` — ${e.meta.by}` : ""}</p>
                    <p className="text-xs text-slate-500">{fmtDate(e.at)} à {(e.at || "").slice(11, 19)} UTC{e.ip ? ` · IP ${e.ip}` : ""}</p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
